import React, { useContext, useEffect, useReducer, useState } from 'react'
import Box from '@mui/material/Box'
import API from '../../../services/API'
import {
  Alert,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material'
import IndeterminateCheckBoxIcon from '@mui/icons-material/IndeterminateCheckBox'
import AddBoxIcon from '@mui/icons-material/AddBox'
import moment from 'moment'
import { useNavigate } from 'react-router-dom'
import AnDateField from '../../../components/DateTime/DateField'
import StaffSelector from '../../events/StaffSelector'
import { UserContext } from '../../../App'
import AnDateTimePicker from '../../../components/DateTime/DateTimePicker'
import EmployeeSelector from '../../events/EmployeeSelector'
import MedicineSelectionCard from './administration/MedicineSelectionCard'
import AdministrationDetailsCard from './administration/AdministrationDetailsCard'
import PatientDetailsCard from './administration/PatientDetailsCard'
import AdministrationActions from './administration/AdministrationActions'
import BagSummaryCard from './administration/BagSummaryCard'
import WitnessPicker from '../../../components/Medicines/WitnessPicker'
import {
  emptyWitness,
  isWitnessComplete,
  toWitnessPayload,
  witnessDisplayName,
} from '../../../helpers/medicineWitness'

const Administration = ({
  bagID,
  submitEPCRModule,
  epcrID,
  setSelectedModule,
  prefill,
}) => {
  const { user } = useContext(UserContext)
  const [contents, setContents] = useState([])
  const [bag, setBag] = useState({})
  const [selectedDrug, setSelectedDrug] = useState('')
  const [batchNumbers, setBatchNumbers] = useState({})
  const [selectedBatchNumber, setSelectedBatchNumber] = useState('')
  const [selectedDrugsAdministered, setSelectedDrugsAdministered] = useState([])
  const [prfNumber, setPrfNumber] = useState('')
  const [administeredBy, setAdministeredBy] = useState(null)
  const [amountMLs, setAmountMLs] = useState('')
  const [stockRemaining, setStockRemaining] = useState(true)
  // Controlled drugs: a colleague other than the administering clinician, or
  // an external person - see helpers/medicineWitness.js
  const [witness, setWitness] = useState(emptyWitness())
  const [patientName, setPatientName] = useState('')
  const [patientDOB, setPatientDOB] = useState(null)
  const [totalDose, setTotalDose] = useState('')
  const [totalDoseType, setTotalDoseType] = useState('mg')
  const [route, setRoute] = useState('')
  const [administeredTime, setAdministeredTime] = useState(
    prefill?.time ? new Date(prefill.time) : new Date(),
  )
  const [isSubmitting, setIsSubmitting] = useState(false)

  const navigate = useNavigate()

  useEffect(() => {
    API.get(
      `/medsmx/bag-contents?id=${bagID}${epcrID ? `&epcrID=${epcrID}` : ''}`,
    )
      .then((res) => {
        setContents(res.data.contents)
        setBag(res.data.bag)
      })
      .catch((e) => console.log(e))
  }, [])

  // Live-capture stub: pre-select the matching medicine and dose once the
  // bag contents have loaded (the crew still picks the batch and confirms)
  const [prefillApplied, setPrefillApplied] = useState(false)
  useEffect(() => {
    if (prefillApplied || !prefill || !contents.length) return
    setPrefillApplied(true)
    const wanted = (prefill.medicineName ?? '').trim().toLowerCase()
    if (wanted) {
      const match = contents.find((item) => {
        const title = (item.medicine?.title ?? '').toLowerCase()
        return title && (title.includes(wanted) || wanted.includes(title))
      })
      if (match) handleDrugNameChange({ target: { value: match.medicine._id } })
    }
    const dose = String(prefill.dose ?? '').match(/^\s*([\d.]+)\s*(mg|g|mcg|ml)\b/i)
    if (dose) {
      setTotalDose(dose[1])
      setTotalDoseType(dose[2].toLowerCase())
    }
  }, [contents, prefill, prefillApplied])

  useEffect(() => {
    if (canReuse) {
      handleQTYchange('increase')
    }
  }, [selectedBatchNumber])

  const drugNames2 = contents.reduce((accumulator, drugItem) => {
    const drug = drugItem.medicine._id
    if (accumulator[drug] == null) accumulator[drug] = []
    accumulator[drug].push({
      title: drugItem.medicine.title,
      reuse: drugItem.medicine.reuse,
      constitution: drugItem.medicine.constitution,
      presentation: drugItem.medicine.presentation,
      route: drugItem.medicine.route,
      isControlled: drugItem.medicine.isControlled,
      _id: drugItem._id,
    })
    return accumulator
  }, {})

  // const drugNameOptions = Object.keys(drugNames)

  const getBatchNumbers = (drugID) => {
    const drug = contents.filter((x) => x.medicine._id === drugID)
    const reducer = drug.reduce((accumulator, x) => {
      if (accumulator[x.batchNumber] == null) accumulator[x.batchNumber] = []
      accumulator[x.batchNumber].push(x)
      return accumulator
    }, {})
    setBatchNumbers(reducer)
  }

  const handleSubmit = () => {
    const selectedMedicineInfo = drugNames2[selectedDrug]?.[0]
    const selectedMedicine = contents.find(
      (x) =>
        x.medicine._id === selectedDrug &&
        x.batchNumber === selectedBatchNumber,
    )

    setIsSubmitting(true)

    API.post('/medsmx/administration', {
      medicines: selectedDrugsAdministered.map((drug) => drug._id),
      medicineName: selectedMedicineInfo?.title,
      medicineDescription: selectedDrug,
      canReuse: canReuse === true,
      amountMLs,
      stockRemaining,
      location: bagID,
      epcrID,
      prfNumber,
      administeredBy: administeredBy?._id,
      witness: isControlled ? toWitnessPayload(witness) : undefined,
      patientName,
      patientDOB,
      totalDose,
      totalDoseType,
      administeredTime,
    })
      .then((res) => {
        if (res.status === 200 && submitEPCRModule) {
          const moduleData = {
            dateTime: administeredTime,
            moduleType: 'trackedMedicineAdministration',
            medicineLogId: res.data?.logId,
            ['Medicine Name']: selectedMedicineInfo?.title ?? '',
            Constitution: selectedMedicineInfo?.constitution ?? '',
            Presentation: selectedMedicineInfo?.presentation ?? '',
            Dose: `${totalDose} ${totalDoseType}`,
            ['Batch Number']: selectedBatchNumber,
            ['Expiry Date']: selectedMedicine?.expiry
              ? moment(selectedMedicine.expiry).format('DD/MM/YYYY')
              : '',
            Route: route || selectedMedicineInfo?.route || '',
            ['From Bag']:
              bag.type === 'Medicine Store'
                ? `${bag.name} (Mobile store)`
                : `${bag.number ? bag.number + ' - ' : ''}${bag.name} (${bag.level})`,
          }

          if (amountMLs) {
            moduleData.Amount = `${amountMLs}ml`
          }
          // Same key the mobile ePCR writes, so the record reads the same
          // whichever device made it
          const witnessName = isControlled ? witnessDisplayName(witness) : ''
          if (witnessName) {
            moduleData['Witnessed By'] = witnessName
          }

          submitEPCRModule('Medicine Administration', moduleData)
          return
        }

        if (res.status === 200) {
          navigate('?success=true')
        }
      })
      .catch((e) => console.error(e))
      .finally(() => setIsSubmitting(false))
  }

  useEffect(() => {
    const selectedMedicineInfo = drugNames2[selectedDrug]?.[0]
    if (selectedMedicineInfo?.route) {
      setRoute(selectedMedicineInfo.route)
    }
  }, [selectedDrug])

  const handleDrugNameChange = (e) => {
    setSelectedDrug(e.target.value)
    getBatchNumbers(e.target.value)
    setSelectedDrugsAdministered([])
    setSelectedBatchNumber('')
    if (drugNames2[e.target.value]?.[0].reuse) {
      handleQTYchange('increase')
    }
  }

  const handleQTYchange = (action) => {
    const filteredDrugs = contents.filter((x) => {
      return (
        x.medicine._id === selectedDrug && x.batchNumber === selectedBatchNumber
      )
    })
    switch (action) {
      case 'increase': {
        setSelectedDrugsAdministered((prev) =>
          filteredDrugs.splice(0, prev.length + 1),
        )
        break
      }
      case 'decrease': {
        setSelectedDrugsAdministered((prev) =>
          filteredDrugs.splice(0, prev.length - 1),
        )
        break
      }
    }
  }

  const canReuse = drugNames2[selectedDrug]?.[0].reuse
  const isControlled = drugNames2[selectedDrug]?.[0].isControlled

  const showPatientDetails =
    selectedBatchNumber && selectedDrug && !submitEPCRModule && !epcrID

  const patientDetailsComplete =
    !showPatientDetails ||
    (patientName?.trim() && patientDOB && prfNumber?.trim())

  return (
    <Stack spacing={2}>
      <BagSummaryCard bag={bag} />

      <MedicineSelectionCard
        drugNames={drugNames2}
        selectedDrug={selectedDrug}
        onSelectDrug={handleDrugNameChange}
        batchNumbers={batchNumbers}
        selectedBatchNumber={selectedBatchNumber}
        setSelectedBatchNumber={setSelectedBatchNumber}
      />

      {selectedBatchNumber && selectedDrug && (
        <AdministrationDetailsCard
          canReuse={canReuse}
          inEpcr={!!epcrID}
          amountMLs={amountMLs}
          setAmountMLs={setAmountMLs}
          stockRemaining={stockRemaining}
          setStockRemaining={setStockRemaining}
          selectedDrugsAdministered={selectedDrugsAdministered}
          handleQTYchange={handleQTYchange}
          totalDose={totalDose}
          setTotalDose={setTotalDose}
          totalDoseType={totalDoseType}
          setTotalDoseType={setTotalDoseType}
          route={route}
          setRoute={setRoute}
          administeredBy={administeredBy}
          setAdministeredBy={setAdministeredBy}
          administeredTime={administeredTime}
          setAdministeredTime={setAdministeredTime}
        />
      )}

      {showPatientDetails && (
        <PatientDetailsCard
          patientName={patientName}
          setPatientName={setPatientName}
          patientDOB={patientDOB}
          setPatientDOB={setPatientDOB}
          prfNumber={prfNumber}
          setPrfNumber={setPrfNumber}
        />
      )}

      {isControlled && (
        <Stack gap={1.5}>
          <Alert severity="warning" variant="outlined" sx={{ py: 0.5 }}>
            Controlled drug - the administration must be witnessed by someone
            other than the administering clinician.
          </Alert>
          <WitnessPicker
            value={witness}
            onChange={setWitness}
            size="medium"
            preventSelectingSelf={false}
            excludeUserIds={[administeredBy?._id]}
          />
        </Stack>
      )}

      <AdministrationActions
        canSubmit={
          ((!canReuse && selectedDrugsAdministered.length > 0) || amountMLs) &&
          patientDetailsComplete &&
          (!isControlled || isWitnessComplete(witness))
        }
        canReuse={canReuse}
        amountMLs={amountMLs}
        selectedDrugsAdministered={selectedDrugsAdministered}
        drugName={drugNames2[selectedDrug]?.[0]?.title}
        isSubmitting={isSubmitting}
        onClose={() => setSelectedModule({})}
        onSubmit={handleSubmit}
      />
    </Stack>
  )
}

export default Administration
